import { useNavigate } from 'react-router'

import { getPageKeyByPath } from '~/app/routes'
import { day, isBetween } from '~/astra'
import { createFilterSliceActionsSelectorByPathname, createFilterSliceSelectorByPathname } from '~/hooks/filterMigration/useFiltersByPathname'
import { useResetSelectedViewOptionProfile } from '~/hooks/tanstack-query/baseClient/viewOptionProfiles/useResetSelectedViewOptionProfile'
import { isSupportedPageKey } from '~/hooks/usePageDrawer'
import { getPageTypeByPathname } from '~/hooks/useViewOptionProfile'
import { markInboxEntryAsRead } from '~/store/di-entity.api'
import { useStore } from '~/store/store'
import {
    InboxEntry,
    isCommentsThreadInboxSource,
    isLocationScheduleThread,
    isScheduleThread,
    isScheduleThreadRef,
    selectCommentThreadById,
    useDateRange,
} from '~/storm'
import { trackUserEvent } from '~/utils/analytics'
import { toInboxEntryData } from '~/utils/analytics/helpers/inboxEntryData'

import { useSelectionDrawer } from '../Drawer/SelectionDrawer/useSelectionDrawer'

export const useInboxEntryHandlers = () => {
    const drawer = useSelectionDrawer()

    const resetSelectedViewOptionProfile = useResetSelectedViewOptionProfile()
    const navigate = useNavigate()
    const setSelectedDate = useStore(state => state.appFilters.actions.setSelectedDate)
    const { startDate, endDate } = useDateRange()

    const openInSelectionDrawer = (entry: InboxEntry) => {
        if (isCommentsThreadInboxSource(entry.source)) {
            if (isScheduleThread(entry.source.comments_thread)) {
                const ref = entry.source.comments_thread.ref
                const pageKey = getPageKeyByPath(entry.source.comments_thread.source_path ?? '')

                const locationId =
                    entry.source.comments_thread.ref.attribute_type === 'location_schedule_thread_ref' ? entry.source.comments_thread.ref.location_id : null
                if (locationId) {
                    drawer.openLocationDateSelectionWithPageDrawer(day(ref.schedule_date), locationId, {
                        pageKey: isSupportedPageKey(pageKey) ? pageKey : undefined,
                    })
                } else {
                    drawer.openDateSelectionWithPageDrawer(day(ref.schedule_date))
                }
            }
        }

        if (!entry.is_read) {
            void markInboxEntryAsRead(entry.id)
        }
    }

    const navigateToSource = async (entry: InboxEntry) => {
        const thread = isCommentsThreadInboxSource(entry.source)
            ? entry.source.comments_thread
            : selectCommentThreadById(useStore.getState(), entry.source.comments_message.thread_id)
        if (!thread) {
            return
        }
        const sourcePath = thread.source_path
        const ref = thread.ref

        if (isScheduleThreadRef(ref)) {
            const scheduleDate = day(ref.schedule_date)

            // Set the date filter if the schedule date is outside the current range
            if (!isBetween(scheduleDate, startDate, endDate, 'day', '[]')) {
                setSelectedDate(scheduleDate)
            }
        }

        if (sourcePath) {
            await navigate(sourcePath)
            const hasFilters = createFilterSliceSelectorByPathname(sourcePath)(useStore.getState()).filters.length > 0
            if (isLocationScheduleThread(thread) && hasFilters) {
                resetSelectedViewOptionProfile.mutate(getPageTypeByPathname(sourcePath))
                createFilterSliceActionsSelectorByPathname(sourcePath)(useStore.getState()).emptyFilters()
            }
            trackUserEvent('INBOX_ENTRY_NAVIGATED_TO_SOURCE', { ...toInboxEntryData(entry), sourcePath })
        }
        openInSelectionDrawer(entry)
    }

    return {
        openInSelectionDrawer,
        navigateToSource,
    }
}
